import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { Form, Row, Col, Select, Input, Button, Space, DatePicker, Tag } from "antd";
import { SearchOutlined, ReloadOutlined, FilterOutlined } from "@ant-design/icons";
import { scenarioContextEnhancedService } from "../../../../api/enhancedApi";
import SupplierSelector from "../../../../components/common/SupplierSelector";
import { STANDARD_COST_TYPE_OPTIONS } from "../../../settlement/constants";
import { normalizeScenarioContextAgentOptions } from "../constants";

const { Option } = Select;
const { RangePicker } = DatePicker;

/**
 * 场景化上下文筛选组件
 *
 * @param {boolean} autoApply - 自动应用模式：表单值变化自动触发 onSearch（防抖 500ms），隐藏搜索/重置按钮
 */
// 高级筛选区包含的字段名（用于判断预设条件是否需要展开高级区域）
const ADVANCED_FILTER_FIELDS = ["date_range", "scenario_supplier_id", "scenario_standard_cost_type", "scenario_knowledge_domain", "prompt_text"];

const ScenarioContextFilter = ({
    onSearch,
    onReset,
    collapsed = false,
    onToggleCollapse,
    autoApply = false,
    /** 预定义筛选条件（如 { scenario_supplier_id: 123 }），写入表单并锁定 */
    predefinedFilters = {},
    /** 当预设条件涉及高级筛选区字段时的回调，父组件可据此自动展开 */
    onPredefinedInAdvanced,
}) => {
    const [form] = Form.useForm();
    const [agentOptions, setAgentOptions] = useState([]);
    const [loading, setLoading] = useState(false);
    const debounceRef = useRef(null);

    // 预设字段集合（用于判断字段是否应被锁定）
    const predefinedFieldKeys = useMemo(
        () => new Set(Object.keys(predefinedFilters).filter((k) => predefinedFilters[k] !== undefined && predefinedFilters[k] !== null)),
        [predefinedFilters]
    );

    // 加载Agent名称列表
    useEffect(() => {
        const loadAgentNames = async () => {
            try {
                setLoading(true);
                const response = await scenarioContextEnhancedService.getAgentNames();
                if (response && response.data) {
                    setAgentOptions(normalizeScenarioContextAgentOptions(response.data.agent_options || response.data.items || []));
                }
            } catch (error) {
                console.error("加载Agent名称失败:", error);
            } finally {
                setLoading(false);
            }
        };

        loadAgentNames();
    }, []);

    // 将 predefinedFilters 写入 Form 并通知父组件是否需要展开高级区域
    useEffect(() => {
        if (predefinedFieldKeys.size === 0) return;
        form.setFieldsValue(predefinedFilters);
        // 检查预设条件是否涉及高级筛选区字段
        const hasAdvanced = [...predefinedFieldKeys].some((key) => ADVANCED_FILTER_FIELDS.includes(key));
        if (hasAdvanced && onPredefinedInAdvanced) {
            onPredefinedInAdvanced();
        }
    }, [predefinedFilters, predefinedFieldKeys, form, onPredefinedInAdvanced]);

    // 处理搜索
    const handleSearch = useCallback(() => {
        form.validateFields().then((values) => {
            // 处理日期范围
            const searchParams = { ...values };

            if (values.date_range && values.date_range.length === 2) {
                searchParams.created_at_start = values.date_range[0].format("YYYY-MM-DD");
                searchParams.created_at_end = values.date_range[1].format("YYYY-MM-DD");
                delete searchParams.date_range;
            }

            onSearch(searchParams);
        });
    }, [form, onSearch]);

    // 处理重置：清空用户输入，但保留预设字段值
    const handleReset = useCallback(() => {
        form.resetFields();
        // 重置后立即回填预设值，确保预设条件不被清除
        if (predefinedFieldKeys.size > 0) {
            form.setFieldsValue(predefinedFilters);
        }
        onReset();
    }, [form, onReset, predefinedFilters, predefinedFieldKeys]);

    // autoApply: 表单值变化自动触发搜索（防抖）
    const handleValuesChange = useCallback(() => {
        if (!autoApply) return;
        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => {
            handleSearch();
        }, 500);
    }, [autoApply, handleSearch]);

    // 基础筛选条件
    const renderBasicFilters = () => (
        <Row gutter={[16, 16]} align="middle">
            <Col xs={24} sm={12} md={6} lg={5}>
                <Form.Item name="agent_name" label="Agent名称">
                    <Select
                        placeholder="请选择Agent名称"
                        style={{ width: "100%" }}
                        allowClear
                        showSearch
                        optionFilterProp="label"
                        filterOption={(input, option) => `${option?.label || ""} ${option?.value || ""}`.toLowerCase().includes(input.toLowerCase())}
                        loading={loading}
                        options={agentOptions}
                    />
                </Form.Item>
            </Col>

            <Col xs={24} sm={12} md={6} lg={4}>
                <Form.Item name="entry_type" label="类型">
                    <Select placeholder="请选择类型" style={{ width: "100%" }} allowClear>
                        <Option value="PROMPT">提示词</Option>
                        <Option value="KNOWLEDGE">知识库</Option>
                        <Option value="CONTEXT">上下文</Option>
                    </Select>
                </Form.Item>
            </Col>

            <Col xs={24} sm={12} md={6} lg={4}>
                <Form.Item name="is_active" label="启用状态">
                    <Select placeholder="请选择状态" style={{ width: "100%" }} allowClear>
                        <Option value={true}>启用</Option>
                        <Option value={false}>停用</Option>
                    </Select>
                </Form.Item>
            </Col>

            <Col xs={24} sm={12} md={6} lg={5}>
                <Form.Item name="description" label="描述关键词">
                    <Input placeholder="请输入描述关键词" allowClear />
                </Form.Item>
            </Col>

            {/* autoApply 模式下隐藏手动操作按钮 */}
            {!autoApply && (
                <Col xs={24} sm={12} md={8} lg={6}>
                    <Form.Item>
                        <Space>
                            <Button type="primary" icon={<SearchOutlined />} onClick={handleSearch} loading={loading}>
                                搜索
                            </Button>
                            <Button icon={<ReloadOutlined />} onClick={handleReset}>
                                重置
                            </Button>
                            {onToggleCollapse && (
                                <Button icon={<FilterOutlined />} onClick={onToggleCollapse} type={collapsed ? "primary" : "default"}>
                                    {collapsed ? "展开" : "收起"}
                                </Button>
                            )}
                        </Space>
                    </Form.Item>
                </Col>
            )}
        </Row>
    );

    // 高级筛选条件
    const renderAdvancedFilters = () => (
        <Row gutter={[16, 16]}>
            <Col xs={24} sm={12} md={8} lg={6}>
                <Form.Item name="date_range" label="创建时间">
                    <RangePicker style={{ width: "100%" }} placeholder={["开始日期", "结束日期"]} format="YYYY-MM-DD" />
                </Form.Item>
            </Col>

            <Col xs={24} sm={12} md={8} lg={6}>
                <Form.Item name="scenario_supplier_id" label={<>{"供应商"}{predefinedFieldKeys.has("scenario_supplier_id") && <Tag color="geekblue" style={{ marginLeft: 4, fontSize: 11, lineHeight: "16px", padding: "0 4px" }}>预设</Tag>}</>}>
                    <SupplierSelector
                        placeholder="请选择供应商"
                        allowClear={!predefinedFieldKeys.has("scenario_supplier_id")}
                        disabled={predefinedFieldKeys.has("scenario_supplier_id")}
                        style={{ width: "100%" }}
                    />
                </Form.Item>
            </Col>

            <Col xs={24} sm={12} md={8} lg={6}>
                <Form.Item name="scenario_standard_cost_type" label="费用类型">
                    <Select
                        placeholder="请选择标准费用类型"
                        options={STANDARD_COST_TYPE_OPTIONS}
                        allowClear
                        showSearch
                        optionFilterProp="label"
                        style={{ width: "100%" }}
                    />
                </Form.Item>
            </Col>

            <Col xs={24} sm={12} md={8} lg={6}>
                <Form.Item name="scenario_knowledge_domain" label="知识领域">
                    <Select placeholder="请选择知识领域" allowClear style={{ width: "100%" }}>
                        <Option value="ap_products">产品知识库</Option>
                        <Option value="ap_billing_rules">计费规则</Option>
                    </Select>
                </Form.Item>
            </Col>

            <Col xs={24} sm={12} md={8} lg={6}>
                <Form.Item name="prompt_text" label="内容关键词">
                    <Input.TextArea placeholder="请输入内容关键词" rows={1} style={{ resize: "none" }} />
                </Form.Item>
            </Col>
        </Row>
    );

    return (
        <Form
            form={form}
            layout="vertical"
            initialValues={{
                is_active: undefined,
            }}
            onValuesChange={handleValuesChange}
        >
            {renderBasicFilters()}
            {!collapsed && renderAdvancedFilters()}
        </Form>
    );
};

export default ScenarioContextFilter;
